// Animated sign-in page components adapted from 21st.dev for CRA + Tailwind.
// Used by TenantLoginPage for BLS tenant sign-in gate.
import {
  memo,
  useState,
  useEffect,
  useRef,
  forwardRef,
} from "react";
import {
  motion,
  useAnimation,
  useInView,
  useMotionTemplate,
  useMotionValue,
} from "motion/react";
import { Eye, EyeOff } from "lucide-react";
import { cn } from "@/lib/utils";

// ==================== Input Component ====================

const Input = memo(
  forwardRef(function Input({ className, type, ...props }, ref) {
    const radius = 100;
    const [visible, setVisible] = useState(false);
    const mouseX = useMotionValue(0);
    const mouseY = useMotionValue(0);

    function handleMouseMove({ currentTarget, clientX, clientY }) {
      const { left, top } = currentTarget.getBoundingClientRect();
      mouseX.set(clientX - left);
      mouseY.set(clientY - top);
    }

    return (
      <motion.div
        style={{
          background: useMotionTemplate`
            radial-gradient(
              ${visible ? radius + "px" : "0px"} circle at ${mouseX}px ${mouseY}px,
              #3b82f6,
              transparent 80%
            )
          `,
        }}
        onMouseMove={handleMouseMove}
        onMouseEnter={() => setVisible(true)}
        onMouseLeave={() => setVisible(false)}
        className="group/input rounded-xl p-[2px] transition duration-300"
      >
        <input
          type={type}
          style={{
            boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05), inset 0 1px 3px rgba(0, 0, 0, 0.04)",
          }}
          className={cn(
            "flex h-12 w-full rounded-xl border border-neutral-300 bg-white px-4 py-3 text-[15px] text-neutral-800 transition duration-200 placeholder:text-neutral-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50",
            className
          )}
          ref={ref}
          {...props}
        />
      </motion.div>
    );
  })
);

Input.displayName = "Input";

// ==================== BoxReveal Component ====================

const BoxReveal = memo(function BoxReveal({
  children,
  width = "fit-content",
  boxColor,
  duration,
  overflow = "hidden",
  position = "relative",
  className,
}) {
  const mainControls = useAnimation();
  const slideControls = useAnimation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (isInView) {
      slideControls.start("visible");
      mainControls.start("visible");
    } else {
      slideControls.start("hidden");
      mainControls.start("hidden");
    }
  }, [isInView, mainControls, slideControls]);

  return (
    <section ref={ref} style={{ position, width, overflow }} className={className}>
      <motion.div
        variants={{
          hidden: { opacity: 0, y: 75 },
          visible: { opacity: 1, y: 0 },
        }}
        initial="hidden"
        animate={mainControls}
        transition={{ duration: duration ?? 0.5, delay: 0.25 }}
      >
        {children}
      </motion.div>
      <motion.div
        variants={{ hidden: { left: 0 }, visible: { left: "100%" } }}
        initial="hidden"
        animate={slideControls}
        transition={{ duration: duration ?? 0.5, ease: "easeIn" }}
        style={{
          position: "absolute",
          top: 4,
          bottom: 4,
          left: 0,
          right: 0,
          zIndex: 20,
          background: boxColor ?? "#022964",
          borderRadius: 4,
        }}
      />
    </section>
  );
});

// ==================== Ripple Component ====================

const Ripple = memo(function Ripple({
  mainCircleSize = 210,
  mainCircleOpacity = 0.24,
  numCircles = 11,
  className = "",
  darkMode = false,
}) {
  return (
    <section
      className={cn(
        "absolute inset-0 flex items-center justify-center",
        className
      )}
      style={{ overflow: "hidden" }}
    >
      {Array.from({ length: numCircles }, (_, i) => {
        const size = mainCircleSize + i * 70;
        const opacity = mainCircleOpacity - i * 0.03;
        const animationDelay = `${i * 0.06}s`;
        const borderStyle = i === numCircles - 1 ? "dashed" : "solid";
        const borderOpacity = darkMode ? (8 + i * 4) : (5 + i * 5);

        return (
          <span
            key={i}
            className="absolute animate-ripple rounded-full"
            style={{
              width: `${size}px`,
              height: `${size}px`,
              opacity,
              animationDelay,
              borderStyle,
              borderWidth: "1px",
              borderColor: darkMode
                ? `rgba(255, 255, 255, ${borderOpacity / 100})`
                : `rgba(2, 41, 100, ${borderOpacity / 100})`,
              backgroundColor: darkMode
                ? `rgba(255, 255, 255, 0.03)`
                : `rgba(0, 0, 0, 0.02)`,
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
            }}
          />
        );
      })}
    </section>
  );
});

// ==================== OrbitingCircles Component ====================

const OrbitingCircles = memo(function OrbitingCircles({
  className,
  children,
  reverse = false,
  duration = 20,
  delay = 10,
  radius = 50,
  path = true,
}) {
  return (
    <>
      {path && (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          version="1.1"
          className="pointer-events-none absolute inset-0 size-full"
        >
          <circle
            className="stroke-white/10 stroke-1"
            cx="50%"
            cy="50%"
            r={radius}
            fill="none"
          />
        </svg>
      )}
      <section
        style={{
          "--duration": duration,
          "--radius": radius,
          "--delay": -delay,
        }}
        className={cn(
          "absolute flex animate-orbit items-center justify-center rounded-full [animation-delay:calc(var(--delay)*1000ms)]",
          { "[animation-direction:reverse]": reverse },
          className
        )}
      >
        {children}
      </section>
    </>
  );
});

// ==================== TechOrbitDisplay Component ====================

const TechOrbitDisplay = memo(function TechOrbitDisplay({
  iconsArray,
  text = "",
}) {
  return (
    <section className="relative flex h-full w-full flex-col items-center justify-center overflow-hidden rounded-lg">
      {text && (
        <span className="pointer-events-none whitespace-pre-wrap bg-gradient-to-b from-white to-white/60 bg-clip-text text-center text-7xl font-semibold leading-none text-transparent">
          {text}
        </span>
      )}
      {iconsArray.map((icon, index) => (
        <OrbitingCircles
          key={index}
          className={icon.className}
          duration={icon.duration}
          delay={icon.delay}
          radius={icon.radius}
          path={icon.path}
          reverse={icon.reverse}
        >
          {icon.component()}
        </OrbitingCircles>
      ))}
    </section>
  );
});

// ==================== AnimatedForm Component ====================

const AnimatedForm = memo(function AnimatedForm({
  header,
  subHeader,
  fields,
  submitButton,
  textVariantButton,
  errorField,
  fieldPerRow = 1,
  onSubmit,
  googleLogin,
  goTo,
  rememberMe,
  onRememberMeChange,
  isSubmitting,
}) {
  const [visible, setVisible] = useState(false);
  const [errors, setErrors] = useState({});

  const toggleVisibility = () => setVisible(!visible);

  const validateForm = (event) => {
    const currentErrors = {};
    fields.forEach((field) => {
      const value = event.target[field.label]?.value;
      if (field.required && !value) {
        currentErrors[field.label] = `${field.label} is required`;
      }
      if (field.type === "email" && value && !/\S+@\S+\.\S+/.test(value)) {
        currentErrors[field.label] = "Invalid email address";
      }
    });
    return currentErrors;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const formErrors = validateForm(event);
    if (Object.keys(formErrors).length === 0) {
      onSubmit(event);
    } else {
      setErrors(formErrors);
    }
  };

  return (
    <section className="max-md:w-full flex flex-col gap-4 w-96 mx-auto">
      <BoxReveal boxColor="var(--skeleton)" duration={0.3}>
        <h2 className="font-bold text-3xl text-neutral-800">{header}</h2>
      </BoxReveal>

      {subHeader && (
        <BoxReveal boxColor="var(--skeleton)" duration={0.3} className="pb-2">
          <p className="text-neutral-500 text-sm max-w-sm leading-relaxed">{subHeader}</p>
        </BoxReveal>
      )}

      {googleLogin && (
        <>
          <BoxReveal
            boxColor="var(--skeleton)"
            duration={0.3}
            overflow="visible"
            width="unset"
          >
            <button
              className="group/btn w-full rounded-xl h-12 font-medium outline-hidden hover:cursor-pointer transition-all duration-200"
              type="button"
              onClick={googleLogin.onClick}
              style={{
                border: "1px solid #e2e5ea",
                background: "#ffffff",
                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.04)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)";
                e.currentTarget.style.borderColor = "#d1d5db";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = "0 1px 3px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.04)";
                e.currentTarget.style.borderColor = "#e2e5ea";
              }}
            >
              <span className="flex items-center justify-center w-full h-full gap-3 text-[15px] text-neutral-700 font-medium">
                <img
                  src={googleLogin.icon}
                  width={20}
                  height={20}
                  alt="Google"
                />
                {googleLogin.text}
              </span>
              <BottomGradient />
            </button>
          </BoxReveal>

          <BoxReveal boxColor="var(--skeleton)" duration={0.3} width="100%">
            <section className="flex items-center gap-4 py-2">
              <hr style={{ flex: 1, border: "none", borderTop: "1px solid #e8eaed" }} />
              <p style={{ color: "#9ca3af", fontSize: 12, fontWeight: 500, letterSpacing: "0.08em", textTransform: "uppercase", margin: 0 }}>or</p>
              <hr style={{ flex: 1, border: "none", borderTop: "1px solid #e8eaed" }} />
            </section>
          </BoxReveal>
        </>
      )}

      <form onSubmit={handleSubmit}>
        <section className="grid grid-cols-1 mb-4">
          {fields.map((field) => (
            <section key={field.label} className="flex flex-col gap-2 mb-2">
              <BoxReveal boxColor="var(--skeleton)" duration={0.3}>
                <Label htmlFor={field.label}>
                  {field.label} <span style={{ color: "#ef4444" }}>*</span>
                </Label>
              </BoxReveal>

              <BoxReveal
                width="100%"
                boxColor="var(--skeleton)"
                duration={0.3}
                className="flex flex-col space-y-2 w-full"
              >
                <section className="relative">
                  <Input
                    type={
                      field.type === "password"
                        ? visible
                          ? "text"
                          : "password"
                        : field.type
                    }
                    id={field.label}
                    name={field.name || field.label}
                    placeholder={field.placeholder}
                    value={field.value}
                    onChange={field.onChange}
                    autoFocus={field.autoFocus}
                  />
                  {field.type === "password" && (
                    <button
                      type="button"
                      onClick={toggleVisibility}
                      className="absolute inset-y-0 right-0 flex items-center"
                      style={{
                        color: "#9ca3af",
                        background: "transparent",
                        border: "none",
                        paddingRight: 14,
                        cursor: "pointer",
                        zIndex: 2,
                      }}
                    >
                      {visible ? (
                        <Eye className="h-4 w-4" />
                      ) : (
                        <EyeOff className="h-4 w-4" />
                      )}
                    </button>
                  )}
                </section>
                <section style={{ height: 16 }}>
                  {errors[field.label] && (
                    <p style={{ color: "#ef4444", fontSize: 12, margin: 0 }}>{errors[field.label]}</p>
                  )}
                </section>
              </BoxReveal>
            </section>
          ))}
        </section>

        {/* Remember me checkbox */}
        {onRememberMeChange && (
          <BoxReveal width="100%" boxColor="var(--skeleton)" duration={0.3}>
            <section className="flex items-center gap-2.5 mb-5">
              <input
                type="checkbox"
                id="remember-me"
                checked={rememberMe}
                onChange={(e) => onRememberMeChange(e.target.checked)}
                style={{
                  width: 18,
                  height: 18,
                  accentColor: "#022964",
                  cursor: "pointer",
                  borderRadius: 5,
                  border: "1.5px solid #d1d5db",
                }}
              />
              <label
                htmlFor="remember-me"
                style={{
                  fontSize: 14,
                  color: "#4b5563",
                  cursor: "pointer",
                  userSelect: "none",
                  fontWeight: 500,
                }}
              >
                Remember me
              </label>
            </section>
          </BoxReveal>
        )}

        <BoxReveal width="100%" boxColor="var(--skeleton)" duration={0.3}>
          {errorField && (
            <p style={{
              color: "#ef4444",
              fontSize: 13,
              marginBottom: 16,
              padding: "10px 14px",
              background: "#fef2f2",
              borderRadius: 8,
              border: "1px solid #fecaca",
            }}>
              {errorField}
            </p>
          )}
        </BoxReveal>

        <BoxReveal
          width="100%"
          boxColor="var(--skeleton)"
          duration={0.3}
          overflow="visible"
        >
          <button
            className="relative group/btn block w-full text-white h-12 outline-hidden hover:cursor-pointer disabled:cursor-not-allowed"
            type="submit"
            disabled={isSubmitting}
            style={{
              background: "#022964",
              border: "1px solid #022964",
              borderRadius: 200,
              fontFamily: "'Supreme-Bold', sans-serif",
              fontSize: 14,
              letterSpacing: "normal",
              transition: "background 0.2s ease, border-color 0.2s ease",
            }}
            onMouseEnter={(e) => {
              if (!e.currentTarget.disabled) {
                e.currentTarget.style.background = "#0f3a7b";
                e.currentTarget.style.borderColor = "#0f3a7b";
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = isSubmitting ? "#9ca3af" : "#022964";
              e.currentTarget.style.borderColor = isSubmitting ? "#9ca3af" : "#022964";
            }}
          >
            {isSubmitting ? "Signing in..." : submitButton}
          </button>
        </BoxReveal>

        {textVariantButton && goTo && (
          <BoxReveal boxColor="var(--skeleton)" duration={0.3}>
            <section style={{ marginTop: 20, textAlign: "center" }}>
              <button
                type="button"
                onClick={goTo}
                style={{
                  fontSize: 14,
                  color: "#022964",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: "4px 0",
                  fontWeight: 500,
                  transition: "color 0.15s ease",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.color = "#0a4a8a"; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = "#022964"; }}
              >
                {textVariantButton}
              </button>
            </section>
          </BoxReveal>
        )}
      </form>
    </section>
  );
});

const BottomGradient = () => {
  return (
    <>
      <span className="group-hover/btn:opacity-100 block transition duration-500 opacity-0 absolute h-px w-full -bottom-px inset-x-0 bg-gradient-to-r from-transparent via-cyan-500 to-transparent" />
      <span className="group-hover/btn:opacity-100 blur-sm block transition duration-500 opacity-0 absolute h-px w-1/2 mx-auto -bottom-px inset-x-10 bg-gradient-to-r from-transparent via-indigo-500 to-transparent" />
    </>
  );
};

// ==================== Label Component ====================

const Label = memo(function Label({ className, ...props }) {
  return (
    <label
      className={cn(
        "text-sm font-medium leading-none",
        className
      )}
      style={{ color: "#374151" }}
      {...props}
    />
  );
});

export {
  Input,
  BoxReveal,
  Ripple,
  OrbitingCircles,
  TechOrbitDisplay,
  AnimatedForm,
  Label,
  BottomGradient,
};
